import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { duplicateContentManager } from '@/utils/duplicateContentManager';
import { technicalSEOManager } from '@/utils/technicalSEO';
import { EnhancedMetaTags } from '@/components/SEO/EnhancedMetaTags';
import { PerformanceOptimizer, WebVitalsOptimizer } from '@/components/SEO/PerformanceOptimizer';

interface SEOData {
  title: string;
  description: string;
  keywords: string[];
  canonicalUrl: string;
  lastModified: string;
}

interface AutoSEOProps {
  pageType: 'homepage' | 'location' | 'blog' | 'service' | 'about' | 'contact';
  customTitle?: string;
  customDescription?: string;
  customKeywords?: string[];
  locationData?: {
    cityName: string;
    stateName: string;
    latitude: number;
    longitude: number;
  };
  articleData?: {
    author: string;
    publishedTime: string;
    modifiedTime: string;
  };
}

export const AutoSEO: React.FC<AutoSEOProps> = ({
  pageType,
  customTitle,
  customDescription,
  customKeywords = [],
  locationData,
  articleData
}) => {
  const location = useLocation();
  const [seoData, setSeoData] = useState<SEOData | null>(null);
  const [canonicalUrl, setCanonicalUrl] = useState('');

  useEffect(() => {
    console.log('AutoSEO effect triggered:', { pageType, pathname: location.pathname });
    
    const generateSEOData = async () => {
      try {
        // CRITICAL: Use www subdomain for canonical URLs to match actual site URL
        const currentUrl = `https://www.carolinatrophyroofs.com${location.pathname}${location.search}`;
        
        // Generate canonical URL - this will normalize and ensure www subdomain
        const canonical = duplicateContentManager.generateCanonicalUrl(currentUrl);
        setCanonicalUrl(canonical);

        let data: SEOData;

        switch (pageType) {
          case 'location':
            if (locationData) {
              const locationSEO = await technicalSEOManager.generateLocationSEO(
                locationData.cityName,
                locationData.stateName
              );
              data = {
                title: customTitle || locationSEO.title,
                description: customDescription || locationSEO.description,
                keywords: [...locationSEO.keywords, ...customKeywords],
                canonicalUrl: canonical,
                lastModified: locationSEO.lastModified
              };
            } else {
              data = getDefaultSEOData();
            }
            break;

          case 'blog':
            data = {
              title: customTitle || 'Carolina Trophy Roofs Blog - Expert Roofing Insights',
              description: customDescription || 'Expert roofing tips, maintenance guides, and industry insights from Carolina Trophy Roofs. Your trusted roofing resource for Eastern North Carolina.',
              keywords: ['roofing blog', 'roof maintenance tips', 'roofing guides', ...customKeywords],
              canonicalUrl: canonical,
              lastModified: new Date().toISOString()
            };
            break;

          case 'homepage':
            data = {
              title: customTitle || 'Best Roofer Near Me NC | Carolina Trophy Roofs | A+ BBB Rated',
              description: customDescription || 'Top-rated roofing contractor in Eastern North Carolina. A+ BBB rating, 15+ years experience. Free estimates, emergency service. Call (252) 302-2003 today!',
              keywords: ['best roofer near me NC', 'roofing contractor', 'roof repair', 'roof replacement', 'North Carolina', ...customKeywords],
              canonicalUrl: canonical,
              lastModified: new Date().toISOString()
            };
            break;

          default:
            data = getDefaultSEOData();
        }

        console.log('Setting SEO data:', data.title);
        setSeoData(data);
      } catch (error) {
        console.error('Error in AutoSEO generateSEOData:', error);
      }
    };

    generateSEOData();
  }, [pageType, location.pathname, customTitle, customDescription]);

  const getDefaultSEOData = (): SEOData => ({
    title: customTitle || 'Carolina Trophy Roofs - Expert Roofing Services NC',
    description: customDescription || 'Professional roofing services in North Carolina. Licensed, insured, and locally owned with A+ BBB rating.',
    keywords: ['roofing services', 'North Carolina', ...customKeywords],
    canonicalUrl: canonicalUrl,
    lastModified: new Date().toISOString()
  });

  if (!seoData) return null;

  return (
    <>
      <EnhancedMetaTags
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonicalUrl={seoData.canonicalUrl}
        ogType={pageType === 'blog' ? 'article' : 'website'}
        articleAuthor={articleData?.author}
        articlePublishedTime={articleData?.publishedTime}
        articleModifiedTime={articleData?.modifiedTime}
        geoRegion={locationData?.stateName}
        geoPlacename={locationData?.cityName}
        geoPosition={locationData ? `${locationData.latitude};${locationData.longitude}` : undefined}
      />
      
      <PerformanceOptimizer
        criticalCSS={`
          .hero-section { display: block; }
          .navigation { display: flex; align-items: center; }
          .logo { display: inline-block; }
          .cta-button { 
            background-color: #ff6b35; 
            color: white; 
            padding: 12px 24px; 
            border-radius: 6px; 
            text-decoration: none;
            font-weight: 600;
            display: inline-block;
          }
          .cta-button:hover { background-color: #e55a2b; }
        `}
        preloadResources={[
          { href: '/fonts/primary-font.woff2', as: 'font', type: 'font/woff2' },
          { href: '/images/logo.webp', as: 'image' }
        ]}
        deferredStyles={[
          '/css/non-critical.css'
        ]}
        lazyImages={true}
      />
      
      <WebVitalsOptimizer
        enableCLS={true}
        enableLCP={true}
        enableFID={true}
      />
    </>
  );
};

export default AutoSEO;